import * as XLSX from 'xlsx';

const LABEL_NOMBRE = 'nombre';
const LABEL_TIPO = 'tipo';
const LABEL_OBLIGATORIO = 'obligatorio';
const LABEL_MINMAX = 'minmax';
const LABEL_FORMATO = 'formato';
const LABEL_RESPUESTAS = 'respuestas';

function normalize(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function classifyLabel(value) {
  const n = normalize(value);
  if (!n) return null;
  if (n.startsWith('nombre')) return LABEL_NOMBRE;
  if (n.startsWith('tipo')) return LABEL_TIPO;
  if (n.startsWith('obligatorio')) return LABEL_OBLIGATORIO;
  if (n.includes('respuestas') && (n.includes('min') || n.includes('max'))) return LABEL_MINMAX;
  if (n.includes('formato')) return LABEL_FORMATO;
  if (n.startsWith('respuestas')) return LABEL_RESPUESTAS;
  return null;
}

function parseObligatorio(value) {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'boolean') return value;
  const n = normalize(value);
  return n === 'si' || n === 'sí' || n === 'yes' || n === 'true' || n === '1' || n === 'obligatorio';
}

function parseMinMax(value) {
  if (value === null || value === undefined || value === '') return { min: null, max: null };
  const str = String(value).trim();
  const match = str.match(/(\d+)\s*[\/\-–]\s*(\d+)/);
  if (match) return { min: Number(match[1]), max: Number(match[2]) };
  const single = str.match(/^\d+$/);
  if (single) return { min: Number(str), max: Number(str) };
  return { min: null, max: null };
}

export function looksLikeDatePattern(str) {
  if (/[\\^${}()|[\]+*?]/.test(str)) return false;
  return /yyyy|yyy/.test(str) || (/MM/.test(str) && (/dd/.test(str) || /yy/.test(str)));
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function datePatternToRegex(pattern) {
  let r = String(pattern).replace(/'/g, '');
  r = escapeRegex(r);
  r = r
    .replace(/yyyy/g, '\\d{4}')
    .replace(/yyy/g, '\\d{3}')
    .replace(/yy/g, '\\d{2}')
    .replace(/MM/g, '\\d{2}')
    .replace(/dd/g, '\\d{2}')
    .replace(/HH/g, '\\d{2}')
    .replace(/hh/g, '\\d{2}')
    .replace(/mm/g, '\\d{2}')
    .replace(/ss/g, '\\d{2}')
    .replace(/SSS/g, '\\d{3}');
  return `^${r}$`;
}

function parseFormato(value) {
  if (value === null || value === undefined || value === '') return null;
  const str = String(value).trim();

  const parts = str.split(/[\/\n]/).map((s) => s.trim());
  let lengthMin = null, lengthMax = null;
  let remainderStart = 0;
  if (parts.length >= 2 && /^\d+$/.test(parts[0]) && /^\d+$/.test(parts[1])) {
    lengthMin = Number(parts[0]);
    lengthMax = Number(parts[1]);
    remainderStart = 2;
  } else if (parts.length >= 1 && /^\d+$/.test(parts[0])) {
    lengthMin = Number(parts[0]);
    remainderStart = 1;
  }

  let regExp = null;
  let datePattern = null;
  const remainder = parts.slice(remainderStart).join('/').trim();
  if (remainder) {
    if (looksLikeDatePattern(remainder)) {
      datePattern = remainder;
      regExp = datePatternToRegex(remainder);
    } else {
      regExp = remainder;
    }
  } else if (lengthMin == null && lengthMax == null) {
    if (looksLikeDatePattern(str)) {
      datePattern = str;
      regExp = datePatternToRegex(str);
    } else {
      regExp = str;
    }
  }

  const result = { lengthMin, lengthMax, regExp };
  if (datePattern) result.datePattern = datePattern;
  return result;
}

function parseRespuestas(value) {
  if (value === null || value === undefined || value === '') return null;
  const str = String(value);
  const tokens = str.split(/[\n;|]/).map((s) => s.trim()).filter(Boolean);
  if (tokens.length === 0) return null;
  return tokens.map((tok) => {
    const parenMatch = tok.match(/^(.+?)\s*\(([^()]+)\)\s*$/);
    if (parenMatch) {
      return { key: parenMatch[2].trim(), value: parenMatch[1].trim() };
    }
    const eq = tok.split('=');
    if (eq.length === 2) return { key: eq[0].trim(), value: eq[1].trim() };
    return { key: tok, value: tok };
  });
}

function findDefinitionSheet(wb) {
  for (const name of wb.SheetNames) {
    const n = normalize(name);
    if (n.startsWith('definicion') || n.startsWith('definición')) return name;
  }
  return wb.SheetNames[wb.SheetNames.length - 1];
}

export function parseDefinitionWorkbook(buffer) {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true });
  const sheetName = findDefinitionSheet(wb);
  const sheet = wb.Sheets[sheetName];
  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: null });
  if (matrix.length === 0) {
    return { formId: null, sheetName, fields: [] };
  }

  const headerRow = matrix[0] || [];
  const fieldIds = [];
  for (let c = 1; c < headerRow.length; c++) {
    const id = headerRow[c];
    if (id !== null && id !== undefined && String(id).trim() !== '') {
      fieldIds.push({ index: c, id: String(id).trim() });
    }
  }

  const labels = new Map();
  for (let r = 1; r < matrix.length; r++) {
    const lbl = classifyLabel(matrix[r]?.[0]);
    if (lbl) labels.set(lbl, r);
  }

  const fields = fieldIds.map(({ index, id }) => {
    const get = (label) => {
      const r = labels.get(label);
      if (r == null) return null;
      return matrix[r]?.[index] ?? null;
    };
    const minmax = parseMinMax(get(LABEL_MINMAX));
    const required = parseObligatorio(get(LABEL_OBLIGATORIO));
    const format = parseFormato(get(LABEL_FORMATO));
    const domain = parseRespuestas(get(LABEL_RESPUESTAS));
    const label = get(LABEL_NOMBRE);
    const type = get(LABEL_TIPO);

    return {
      id,
      label: label != null ? String(label).trim() : id,
      type: type != null ? String(type).trim().toLowerCase() : null,
      validation: {
        mandatory: { required, min: minmax.min, max: minmax.max },
        format,
        domain,
      },
    };
  });

  const formIdMatch = wb.Props?.Title || sheetName.match(/CAT\d+/)?.[0] || null;

  return {
    formId: formIdMatch || sheetName,
    sheetName,
    fields,
  };
}

export async function parseDefinitionFromFile(file) {
  const buffer = await file.arrayBuffer();
  return parseDefinitionWorkbook(buffer);
}
